import { useTranslation } from 'react-i18next';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { ChallengeBanner } from '../components/ChallengeExerciseNav';
import ExerciseOutcomeActions from '../components/ExerciseOutcomeActions';
import ExerciseWorkspace from '../components/ExerciseWorkspace';
import SubmissionFeedback from '../components/SubmissionFeedback';
import BadgeAwardAnnouncement from '../features/badges/BadgeAwardAnnouncement';
import { FIRST_EXERCISE_ID } from '../features/exercises/exerciseApi';
import { useExerciseData } from '../features/exercises/useExerciseData';
import { useExerciseOutcome } from '../features/exercises/useExerciseOutcome';
import { useExerciseSubmission } from '../features/exercises/useExerciseSubmission';
import { useChallengeExercises } from '../features/leaderboard/useChallengeExercises';
import type { ChallengeExercises } from '../features/leaderboard/leaderboardApi';

import type { ExerciseData, SubmissionData } from '../features/exercises/exerciseTypes';

const GENERIC_WRONG_ANSWER_CODE = 'INVALID_ANSWER';

function SubmissionFeedbackPanel({
  verdict,
  cooldownSeconds,
  attemptsExhausted,
}: {
  verdict: SubmissionData | null;
  cooldownSeconds: number;
  attemptsExhausted: boolean;
}) {
  if (!verdict) {
    return null;
  }

  // The generic wrong-answer code adds nothing for students: the banner already says it.
  const displayedError = verdict.error === GENERIC_WRONG_ANSWER_CODE ? null : verdict.error;

  return (
    <SubmissionFeedback
      result={{
        passed: verdict.passed,
        pointsEarned: verdict.pointsEarned,
        output: verdict.executionOutput ?? displayedError ?? undefined,
        error: displayedError,
      }}
      cooldownSeconds={cooldownSeconds}
      attemptsExhausted={attemptsExhausted}
    />
  );
}

function ExerciseStatus({ isPending, hints }: { isPending: boolean; hints?: string }) {
  const { t } = useTranslation();
  return (
    <>
      {isPending ? <p role="status">{t('exercise.pending')}</p> : null}
      {hints ? <p>Hint: {hints}</p> : null}
    </>
  );
}

function menuLink(courseId?: string) {
  return courseId ? `/courses/${courseId}` : '/courses';
}

interface ChallengeContext {
  courseId: string;
  exercises: ChallengeExercises;
}

/** Challenge mode is on when the URL carries `?challenge={courseId}` and that challenge is still live. */
function useChallengeContext(): ChallengeContext | null {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get('challenge');
  const exercises = useChallengeExercises(courseId);
  return courseId && exercises ? { courseId, exercises } : null;
}

function NavigationPanel({ courseId }: { courseId?: string }) {
  return (
    <section
      aria-label="Navigation"
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.75rem',
        alignItems: 'center',
        padding: '0.75rem 1rem',
        border: '1px solid var(--color-border)',
        borderRadius: '0.75rem',
        background: 'var(--color-bg-elevated)',
      }}
    >
      <Link to={menuLink(courseId)}>&larr; Retour au menu</Link>
    </section>
  );
}

function ExercisePageContent({
  exercise,
  submission,
  isSubmitting,
  submitCommand,
  submitQuiz,
  challenge,
}: {
  exercise: ExerciseData;
  submission: SubmissionData | null;
  isSubmitting: boolean;
  submitCommand: (command: string) => void | Promise<void>;
  submitQuiz: (selectedOptionIds: string[]) => void | Promise<void>;
  challenge: ChallengeContext | null;
}) {
  const outcome = useExerciseOutcome(submission, isSubmitting);

  return (
    <main
      style={{
        maxWidth: 720,
        margin: '3rem auto',
        padding: '0 1rem',
        display: 'grid',
        gap: '1.5rem',
      }}
    >
      {challenge ? (
        <ChallengeBanner courseId={challenge.courseId} challenge={challenge.exercises} />
      ) : null}
      <header>
        <p
          style={{
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.75rem',
            color: 'var(--color-text-muted)',
          }}
        >
          {exercise.type ?? 'Exercise'}
        </p>
        <h1>{exercise.title}</h1>
        <p>{exercise.description}</p>
        <p>
          <strong>{exercise.points ?? 0}</strong> points
        </p>
      </header>

      <SubmissionFeedbackPanel
        verdict={outcome.verdict}
        cooldownSeconds={outcome.cooldownSeconds}
        attemptsExhausted={outcome.attemptsExhausted}
      />
      <SubmissionBadgeAnnouncement submission={submission} />
      {outcome.kind ? (
        <ExerciseOutcomeActions
          kind={outcome.kind}
          exerciseId={exercise.id}
          courseId={exercise.courseId}
          challenge={challenge}
        />
      ) : null}
      <ExerciseWorkspace
        exercise={exercise}
        submission={submission}
        isSubmitting={outcome.inputsDisabled}
        locked={outcome.locked}
        onSubmitCommand={submitCommand}
        onSubmitQuiz={submitQuiz}
      />
      <ExerciseStatus isPending={outcome.isPending} hints={exercise.hints} />
      <NavigationPanel courseId={exercise.courseId} />
    </main>
  );
}

function SubmissionBadgeAnnouncement({ submission }: { submission: SubmissionData | null }) {
  if (
    submission?.passed !== true ||
    (submission.badgeAwardingConfirmed === undefined &&
      (submission.newlyAwardedBadges?.length ?? 0) === 0)
  ) {
    return null;
  }

  return (
    <BadgeAwardAnnouncement
      awards={submission.newlyAwardedBadges ?? []}
      confirmed={submission.badgeAwardingConfirmed}
    />
  );
}

function ExerciseScreen({ exerciseId }: { exerciseId: string }) {
  const { exercise, isLoading, error: loadError } = useExerciseData(exerciseId);
  const challenge = useChallengeContext();
  const submissionExerciseId = exercise?.id ?? exerciseId;
  const {
    submission,
    isSubmitting,
    error: submissionError,
    submitCommand,
    submitQuiz,
  } = useExerciseSubmission(submissionExerciseId);

  if (isLoading) {
    return (
      <main style={{ maxWidth: 720, margin: '3rem auto', padding: '0 1rem' }}>
        Loading challenge...
      </main>
    );
  }

  if (loadError || submissionError) {
    return (
      <main style={{ maxWidth: 720, margin: '3rem auto', padding: '0 1rem' }}>
        <p role="alert">{loadError ?? submissionError}</p>
      </main>
    );
  }

  if (!exercise) {
    return null;
  }

  return (
    <ExercisePageContent
      exercise={exercise}
      submission={submission}
      isSubmitting={isSubmitting}
      submitCommand={submitCommand}
      submitQuiz={submitQuiz}
      challenge={challenge}
    />
  );
}

function ExercisePage({ exerciseId: propExerciseId }: { exerciseId?: string }) {
  const { exerciseId: routeExerciseId } = useParams();
  const resolvedExerciseId = propExerciseId ?? routeExerciseId ?? FIRST_EXERCISE_ID;

  // Keyed by exercise so answers, attempts and locking never leak into the next exercise.
  return <ExerciseScreen key={resolvedExerciseId} exerciseId={resolvedExerciseId} />;
}

export default ExercisePage;
